import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import { Table, Spin, Empty, Button, Select as AntSelect, Tag, Pagination } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt } from '@fortawesome/pro-duotone-svg-icons';
import { Bar } from 'react-chartjs-2';
import { notificationsAtom } from '../../../../atoms/Atoms';
import {
  getPipelineHealthDomains,
  getPipelineHealthEntityTypes,
  getPipelineHealthSnapshots,
  getPipelineHealthSources
} from '../../../../api/pipelineHealthApi';
import {
  formatDomain,
  scoreColor,
  statusToColor
} from '../pipelineHealthUtils';
import { thousand } from '../../../../functions';

function SnapshotFiles() {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [domains, setDomains] = useState([]);
  const [domain, setDomain] = useState('');
  const [limit, setLimit] = useState(500);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invPage, setInvPage] = useState(1);
  const [invPageSize, setInvPageSize] = useState(20);
  const [sources, setSources] = useState([]);
  const [sourceFilter, setSourceFilter] = useState('');
  const [entityTypes, setEntityTypes] = useState([]);
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');

  const pushError = (description) => {
    setNotifications([...notifications, { title: t('error'), description, error: true }]);
  };

  // Fetch domain + source lists once on mount so the selectors are populated.
  useEffect(() => {
    getPipelineHealthDomains()
      .then((res) => {
        const list = res?.data?.data ?? [];
        setDomains(list);
        if (list.length > 0) setDomain(list[0]);
      })
      .catch((err) => pushError(err?.response?.data?.message || err.message));
    getPipelineHealthSources()
      .then((res) => setSources(res?.data?.data ?? []))
      .catch((err) => pushError(err?.response?.data?.message || err.message));
  }, []);

  // Entity-types are scoped to the current domain. Returns [{type, entities}].
  useEffect(() => {
    if (!domain) {
      setEntityTypes([]);
      return;
    }
    getPipelineHealthEntityTypes(domain)
      .then((res) => setEntityTypes(res?.data?.data ?? []))
      .catch((err) => pushError(err?.response?.data?.message || err.message));
  }, [domain]);

  const load = () => {
    if (!domain) return;
    setLoading(true);
    getPipelineHealthSnapshots(domain, limit)
      .then((res) => {
        if (res?.data?.success === false) {
          pushError(res?.data?.error || t('error'));
          return;
        }
        setData(res?.data?.data ?? res?.data);
      })
      .catch((err) => pushError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [domain, limit]);

  // ---- Filter option lists ------------------------------------------------

  // Domain dropdown narrows to the selected source's domains; falls back
  // to the canonical /domains list when no source is picked or sources
  // haven't loaded yet.
  const availableDomains = useMemo(() => {
    if (sourceFilter) {
      const src = sources.find((s) => s.source === sourceFilter);
      return src?.domains || [];
    }
    return domains;
  }, [sources, sourceFilter, domains]);

  // If the user picks a source whose domains don't include the current one,
  // snap the domain to the first one available for that source.
  useEffect(() => {
    if (availableDomains.length === 0) return;
    if (!domain || !availableDomains.includes(domain)) {
      setDomain(availableDomains[0]);
    }
  }, [availableDomains, domain]);

  // Entity dropdown narrows to the selected entity-type's entities.
  // No type → union of every type's entities for the current domain.
  const availableEntities = useMemo(() => {
    if (entityTypeFilter) {
      const grp = entityTypes.find((g) => g.type === entityTypeFilter);
      return grp?.entities || [];
    }
    return Array.from(
      new Set(entityTypes.flatMap((g) => g.entities || []))
    ).sort();
  }, [entityTypes, entityTypeFilter]);

  // Reset entity / entityType filters when their options change.
  useEffect(() => {
    if (entityTypeFilter && !entityTypes.find((g) => g.type === entityTypeFilter)) {
      setEntityTypeFilter('');
    }
  }, [entityTypes, entityTypeFilter]);
  useEffect(() => {
    if (entityFilter && !availableEntities.includes(entityFilter)) {
      setEntityFilter('');
    }
  }, [availableEntities, entityFilter]);

  // Reset inventory pagination when the underlying dataset or filters change.
  useEffect(() => {
    setInvPage(1);
  }, [domain, limit, sourceFilter, entityTypeFilter, entityFilter]);

  // ---- Volume-by-type bar chart ----------------------------------------

  const volumeChart = useMemo(() => {
    const rows = data?.volumeByFileType || [];
    if (rows.length === 0) return null;
    // Sort descending by totalRecords so the biggest types read left-to-right.
    const sorted = [...rows].sort((a, b) => Number(b.totalRecords) - Number(a.totalRecords));
    return {
      labels: sorted.map((r) => r.entityType),
      datasets: [
        {
          label: t('pipeline_health_records'),
          data: sorted.map((r) => Number(r.totalRecords) || 0),
          backgroundColor: '#2563eb',
          borderColor: '#2563eb'
        }
      ]
    };
  }, [data, t]);

  const volumeOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${t('pipeline_health_records')}: ${thousand(ctx.parsed.y)}`
        }
      }
    },
    scales: {
      y: { beginAtZero: true, ticks: { callback: (v) => thousand(v) } },
      x: { ticks: { font: { size: 10 } } }
    }
  }), [t]);

  // ---- Inventory table -------------------------------------------------

  const inventoryColumns = [
    { title: t('entity'), dataIndex: 'entityName', key: 'entityName' },
    {
      title: t('pipeline_health_snapshots_file_date'),
      dataIndex: 'fileDate',
      key: 'fileDate',
      width: 140,
      render: (d) => (d ? dayjs(d).format('MMM DD, YYYY') : '—')
    },
    {
      title: t('pipeline_health_snapshots_file_type'),
      dataIndex: 'entityType',
      key: 'entityType',
      width: 170
    },
    {
      title: t('pipeline_health_records'),
      dataIndex: 'records',
      key: 'records',
      width: 120,
      align: 'right',
      render: (v) => (v == null ? '—' : thousand(v))
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (s) => <Tag color={statusToColor(s)}>{t(s?.toLowerCase() || 'unknown')}</Tag>
    }
  ];

  // ----------------------------------------------------------------------

  // The server returns the full domain dataset; we apply the entityType /
  // entity filters on the client so the user can switch quickly without a
  // round-trip.
  const inventory = useMemo(() => {
    let rows = data?.inventory || [];
    if (entityTypeFilter) rows = rows.filter((r) => r.entityType === entityTypeFilter);
    if (entityFilter) rows = rows.filter((r) => r.entityName === entityFilter);
    return rows;
  }, [data, entityTypeFilter, entityFilter]);

  // Volume-by-type chart reflects the same filtering as the table — when
  // an entity is picked, the chart shows only that entity's per-type
  // breakdown; when only a type is picked, the chart collapses to that
  // single bar.
  const volumeByType = useMemo(() => {
    if (!entityTypeFilter && !entityFilter) return data?.volumeByFileType || [];
    const rows = inventory;
    const byType = new Map();
    rows.forEach((r) => {
      const key = r.entityType || '—';
      const prev = byType.get(key) || 0;
      byType.set(key, prev + (Number(r.records) || 0));
    });
    return Array.from(byType.entries()).map(([entityType, totalRecords]) => ({ entityType, totalRecords }));
  }, [data, inventory, entityTypeFilter, entityFilter]);

  const sourceOptions = [
    { value: '', label: t('pipeline_health_all_sources') },
    ...sources.map((s) => ({ value: s.source, label: formatDomain(s.source) }))
  ];
  const entityTypeOptions = [
    { value: '', label: t('pipeline_health_all_entity_types') },
    ...entityTypes.map((g) => ({ value: g.type, label: g.type }))
  ];
  const entityOptions = [
    { value: '', label: t('pipeline_health_all_entities') },
    ...availableEntities.map((e) => ({ value: e, label: e }))
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-wrap items-center gap-3 px-2 pb-2">
        <span className="text-sm text-gray-600">{t('pipeline_health_source_filter')}:</span>
        <AntSelect
          value={sourceFilter}
          disabled={loading}
          onChange={setSourceFilter}
          style={{ minWidth: 160 }}
          options={sourceOptions}
        />
        <span className="text-sm text-gray-600">{t('domain')}:</span>
        <AntSelect
          value={domain}
          disabled={loading}
          onChange={setDomain}
          style={{ minWidth: 160 }}
          options={availableDomains.map((d) => ({ value: d, label: formatDomain(d) }))}
        />
        <span className="text-sm text-gray-600">{t('pipeline_health_entity_type')}:</span>
        <AntSelect
          value={entityTypeFilter}
          disabled={loading}
          onChange={setEntityTypeFilter}
          style={{ minWidth: 160 }}
          options={entityTypeOptions}
        />
        <span className="text-sm text-gray-600">{t('entity')}:</span>
        <AntSelect
          showSearch
          value={entityFilter}
          disabled={loading}
          onChange={setEntityFilter}
          style={{ minWidth: 200 }}
          options={entityOptions}
          optionFilterProp="label"
        />
        <span className="text-sm text-gray-600">{t('pipeline_health_snapshots_limit_label')}:</span>
        <AntSelect
          value={limit}
          disabled={loading}
          onChange={setLimit}
          style={{ width: 100 }}
          options={[
            { value: 100, label: '100' },
            { value: 250, label: '250' },
            { value: 500, label: '500' },
            { value: 1000, label: '1000' }
          ]}
        />
        {data?.latestSnapshotDate && (
          <span className="text-sm text-gray-500">
            {t('pipeline_health_latest_date')}: {dayjs(data.latestSnapshotDate).format('MMM DD, YYYY')}
          </span>
        )}
        <Button
          className="ml-auto"
          type="primary"
          onClick={load}
          loading={loading}
          icon={<FontAwesomeIcon icon={faSyncAlt} />}
        >
          {t('refresh')}
        </Button>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-10"><Spin /></div>
      ) : !data ? (
        <Empty description={t('no_data')} />
      ) : (
        <div className="px-2 pb-4 space-y-4">

          {/* Volume by file type */}
          <div className="bg-white rounded shadow-sm p-3">
            <div className="text-sm font-semibold mb-2">
              {t('pipeline_health_snapshots_volume_by_type')}
            </div>
            {volumeByType.length === 0 ? (
              <Empty description={t('no_data')} />
            ) : (
              <Spin spinning={loading}>
                <div style={{ height: 280 }}>
                  <Bar data={volumeChart} options={volumeOptions} />
                </div>
              </Spin>
            )}
          </div>

          {/* Inventory table */}
          <div className="bg-white rounded shadow-sm p-3">
            <div className="text-sm font-semibold mb-2">
              {t('pipeline_health_snapshots_inventory')} ({inventory.length})
            </div>
            {inventory.length === 0 ? (
              <Empty description={t('no_data')} />
            ) : (
              <>
                <Table
                  size="small"
                  loading={loading}
                  rowKey={(row, idx) => `${row.entityName}-${row.fileDate}-${idx}`}
                  columns={inventoryColumns}
                  dataSource={inventory}
                  pagination={{ hideOnSinglePage: true }}
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SnapshotFiles;
